import { lazy, Suspense, useEffect, useState } from "react";
import Scrollbars from "react-custom-scrollbars";
import BallGridPulseLoader from "components/common/BallGridPulseLoader/BallGridPulseLoader";

// hooks and utilities
import { useLanguage, useTranslateFunction } from "lang/Translate";

// styles
import "./Overlay.scoped.scss";

// lazy components
const Information = lazy(() => import("components/overlay/information/Information"));
const About = lazy(() => import("components/overlay/about/About"));
const Help = lazy(() => import("components/overlay/help/Help"));
const Profile = lazy(() => import("components/overlay/profile/Profile"));
const Group = lazy(() => import("components/overlay/group/Group"));
const Research = lazy(() => import("components/overlay/research/Research"));
const Settings = lazy(() => import("components/overlay/settings/Settings"));

const Overlay = () => {
	const [view, setView] = useState("");
	const [hidden, setHidden] = useState(true);
	const [initialTab, setInitialTab] = useState("");
	const [scrollHeight, setScrollHeight] = useState(0);
	const [navCollapsed, setNavCollapsed] = useState(false);
	const [viewContainer, setViewContainer] = useState(null);

	const lang = useLanguage();
	const translate = useTranslateFunction();
	const c = "Overlay";

	const closeOverlay = () => {
		setHidden(true);
		setTimeout(() => setView(""), 1000);
	};

	const back = () => setView(initialTab);

	useEffect(() => {
		window.showOverlay = subtab => {
			setHidden(subtab === "");

			if (typeof subtab === "string") {
				setView(subtab);
				setInitialTab(subtab);
			}
		};
	}, []);

	useEffect(() => {
		if (!viewContainer) return;
		const resizeObserver = new ResizeObserver(([v]) => setScrollHeight(v.target.getBoundingClientRect().height));
		resizeObserver.observe(viewContainer);

		return () => resizeObserver.unobserve(viewContainer);
	}, [viewContainer]);

	return (
		<div className={`overlay ${hidden && "hidden"}`}>
			<nav className="back" onClick={closeOverlay}>
				<p>
					<i className="fas fa-arrow-left" /> {translate("RETURN", "General")}
				</p>
			</nav>

			<div className="overlay__container">
				<nav className={`primary ${navCollapsed && "collapsed"}`}>
					<ul>
						<li className="header">
							<i className="fas fa-angle-left" onClick={() => setNavCollapsed(c => !c)} />
						</li>

						<li className={`${view === "profile" && "active"}`} onClick={() => setView("profile")}>
							<i className="fas fa-user" />
							<p>{translate("PROFILE", c)}</p>
						</li>

						<li className={`${view === "research" && "active"}`} onClick={() => setView("research")}>
							<i className="fas fa-flask" />
							<p>{translate("RESEARCH", c)}</p>
						</li>

						<li className={`${view === "group" && "active"}`} onClick={() => setView("group")}>
							<i className="fas fa-group-arrows-rotate" />
							<p>{translate("GROUP", c)}</p>
						</li>

						<li className={`${view === "settings" && "active"}`} onClick={() => setView("settings")}>
							<i className="fas fa-gear" />
							<p>{translate("SETTINGS", c)}</p>
						</li>

						<li className={`${view === "about" && "active"}`} onClick={() => setView("about")}>
							<i className="fas fa-info" />
							<p>{translate("ABOUT", c)}</p>
						</li>
					</ul>
				</nav>

				<Scrollbars
					className="scroll_container"
					hideTracksWhenNotNeeded={true}
					renderTrackVertical={props => <div {...props} className="track_vertical" />}
					renderThumbVertical={props => <div {...props} className="thumb_vertical" />}
					style={{ height: scrollHeight }}
				>
					<div className="overlay__container__view" ref={setViewContainer}>
						<Suspense fallback={<BallGridPulseLoader center={true} color="hsl(215, 50%, 23%)" />}>
							{view === "about" && <About setView={setView} close={closeOverlay} />}
							{view === "info" && <Information lang={lang} back={back} />}
							{view === "help" && <Help />}
							{view === "profile" && <Profile />}
							{view === "group" && <Group />}
							{view === "research" && <Research />}
							{view === "settings" && <Settings />}
						</Suspense>
					</div>
				</Scrollbars>
			</div>
		</div>
	);
};

export default Overlay;
